import { Action, ActionFunction1 } from "redux-actions";
import { call, put, select } from "redux-saga/effects";

import { AxiosInterceptedResult } from "../../../api/interceptors";
import { IDisplayInfoResponse, IResolveResponse, ResolvableType } from "../../../api/ResolveApi/resolve-api.types";
import { IClientPatch, IPatchAction, IPatchPayload } from "../../../components/course-author/helpers/patches";
import { patchPayloadCreator } from "../../../components/course-author/helpers/patches/creators";
import { dependencies } from "../../../dependencies";
import {
  IFullScormPackage,
  IStateResponse,
  resolveScormPackagePostSuccessResponseValueToScormPackageUIData,
} from "../../../parsers/resolve";
import { SagaAction } from "../../../sagas/types";
import * as selectors from "../../../selectors/selectors";
import { IScormPackage } from "../../types/common";
import { IInitialData } from "./";

const { api } = dependencies;

interface IFurtherActions {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<IInitialData, Action<IInitialData>>;
}

function* getScormPackageById(
  scormPackageId: string,
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>
) {
  const accountId: string = yield select(selectors.accountId);
  const organizationId: string = yield select(selectors.organizationId);
  const [err, res]: AxiosInterceptedResult<Array<IResolveResponse<IStateResponse<IFullScormPackage>>>> = yield call(
    api.resolve.scormPackage.post,
    accountId,
    [scormPackageId],
    organizationId
  );
  if (err || res === null || !res.data.length) {
    yield put(onFailure({ message: "Error resolving scormPackage" }));
    return yield null;
  }
  return yield res.data[0];
}

export const fetchInitialData = (fa: IFurtherActions) =>
  function* _fetchInitialData(): SagaAction {
    const accountId: string = yield select(selectors.accountId);
    const scormPackageId: string = yield select(selectors.currentScormPackageId);
    const fullScormPackageResp: IStateResponse<IFullScormPackage> | null = yield getScormPackageById(
      scormPackageId,
      fa.onFailure
    );
    if (fullScormPackageResp === null) {
      return;
    }
    const scormPackage: IScormPackage = {
      scormPackageId,
      ...resolveScormPackagePostSuccessResponseValueToScormPackageUIData(fullScormPackageResp),
    };
    const [crsErr, crsResp]: AxiosInterceptedResult<Array<IResolveResponse<IDisplayInfoResponse>>> = yield call(
      api.resolve.displayInfo.post,
      accountId,
      [[ResolvableType.Course, scormPackage.courseId]]
    );
    if (crsErr || crsResp === null || !crsResp.data.length) {
      yield put(fa.onFailure({ message: "Could not get course for scorm pacakge" }));
      return;
    }
    const [mmbrsErr, mmbrsResp]: AxiosInterceptedResult<string[]> = yield call(
      api.users.getMembersByScormPackage,
      accountId,
      scormPackageId
    );
    if (mmbrsErr || mmbrsResp === null) {
      yield put(fa.onFailure({ message: "Could not get member ids" }));
      return;
    }
    yield put(
      fa.onSuccess({
        courseName: crsResp.data[0].value.displayName || "",
        memberIds: mmbrsResp.data,
        scormPackage,
      })
    );
  };

interface IPatchFurtherActions {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<void, Action<void>>;
  refreshScormPackage: {
    onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
    onSuccess: ActionFunction1<IScormPackage, Action<IScormPackage>>;
  };
}

export const patchScormPackage = (fa: IPatchFurtherActions) =>
  function* _patchScormPackage(action: Action<IClientPatch<IPatchAction>>): SagaAction {
    const accountId: string = yield select(selectors.accountId);
    const scormPackage: IScormPackage = yield select(selectors.currentScormPackage);
    const patchPayload: IPatchPayload = yield call(patchPayloadCreator, scormPackage.versionId, action.payload);
    const [err, resp]: AxiosInterceptedResult<{
      baseVersionId: number;
      numClientPatches: number;
    }> = yield call(api.scormPackages.patchScormPackage, accountId, scormPackage.scormPackageId, patchPayload);
    if (err || resp === null) {
      yield put(fa.onFailure({ message: "Error on patching scorm package" }));
      return;
    }
    const fullScormPackageResp: IStateResponse<IFullScormPackage> = yield call(
      getScormPackageById,
      scormPackage.scormPackageId,
      fa.refreshScormPackage.onFailure
    );
    yield put(
      fa.refreshScormPackage.onSuccess({
        scormPackageId: scormPackage.scormPackageId,
        ...resolveScormPackagePostSuccessResponseValueToScormPackageUIData(fullScormPackageResp),
      })
    );
    yield put(fa.onSuccess());
  };
